import Features from "@components/Features";
import FrequentlyAskedQuestions from "@components/FrequentlyAskedQuestions";
import LimitedTimeOfferDialog from "@components/LimitedTimerOfferDialog";
import Plans from "@components/Plans";
import SubscriptionForm from "@components/SubscriptionForm";
import SubscriptionFormTimer from "@components/SubscriptionFormTimer";
import localizeCurrency from "@helpers/localizeCurrency";
import ErrorPage from "@pages/ErrorPage";
import LoadingPage from "@pages/LoadingPage";
import { Suspense, useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Await, useLoaderData } from "react-router-dom";
import {
  GetPricesData,
  GetPricesLoaderResult,
} from "src/loaders/getPricesLoader";
import { Plan } from "src/types";

export default function PlansPage() {
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const [selectedPlan, setSelectedPlan] = useState<Plan>("yearly")

  const data = useLoaderData() as GetPricesLoaderResult;
  const { t } = useTranslation();

  const [timeLeft, setTimeLeft] = useState("00:00")

  useEffect(() => {
    const startMinutes = 9
    setTimeLeft("09:00")

    const endTime = new Date().getTime() + startMinutes * 60 * 1000

    const timer = setInterval(() => {
      const now = new Date().getTime()
      const distance = endTime - now

      if (distance <= 0) {
        clearInterval(timer)
        setTimeLeft("00:00")
        return
      }

      const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60))
      const seconds = Math.floor((distance % (1000 * 60)) / 1000)

      setTimeLeft(`${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`)
    }, 1000)

    return () => clearInterval(timer)
  }, [])

  useEffect(() => {
    const timeout = setTimeout(() => {
      setIsDialogOpen(true)
    }, 30000)

    return () => clearTimeout(timeout)
  }, [])

  const getPriceId = (data: GetPricesData) => {
    const isEligibleForIntroPrice =
      data.subscriptionStatus === "none"

    const introPrice =
      selectedPlan === "yearly"
        ? data.yearlyIntroPrice
        : data.monthlyIntroPrice
    const regularPrice =
      selectedPlan === "yearly"
        ? data.yearlyPrice
        : data.monthlyPrice

    const priceId = isEligibleForIntroPrice ? introPrice?.id : regularPrice?.id

    return priceId
  }

  const getPriceInCents = (getIntroPrice: boolean, data: GetPricesData) => {
    const introPrice =
      selectedPlan === "yearly"
        ? data.yearlyIntroPrice
        : data.monthlyIntroPrice
    const regularPrice =
      selectedPlan === "yearly"
        ? data.yearlyPrice
        : data.monthlyPrice

    const price = getIntroPrice ? introPrice : regularPrice
    const localizePrice = localizeCurrency({
      amountInCents: parseFloat(price?.currency_options?.[data.currency]?.unit_amount_decimal ?? price?.unit_amount_decimal ?? "0"),
      currency: data.currency,
    })

    return localizePrice
  }


  if (isSubmitting) return <LoadingPage />;

  return (
    <Suspense fallback={<LoadingPage />}>
      <Await errorElement={<ErrorPage />} resolve={data.getPricesData}>
        {(getPricesData: GetPricesData) => (
          <>
           {timeLeft !== "00:00" && (
              <div className="top-0 left-0 right-0 p-4 bg-[#B3ACFC]">
                <SubscriptionFormTimer
                  buttonSubtitle={t("pages.checkout.plans.reservedFor50", { countdown: timeLeft })}
                  currency={getPricesData.currency}
                  isSubmitting={isSubmitting}
                  periodAmount={1}
                  plan="yearly"
                  priceId={getPricesData.subscriptionStatus === "none" ? getPricesData.yearlyIntroPrice?.id : getPricesData.yearlyPrice?.id}
                  setIsSubmitting={setIsSubmitting}
                  stripePrice={getPricesData.yearlyPrice}
                  timeLeft={timeLeft}
                />
              </div>
            )}
            <div className="custom-container pb-14 pt-8">
              <h1 className="custom-heading mb-14">
                <Trans
                  components={{
                    colored: <span className="custom-text-colored" />,
                  }}
                  i18nKey="pages.checkout.plans.heading"
                />
              </h1>

              <section className="mb-16">
                <Plans
                  eligibleForIntroPrice={
                    getPricesData?.subscriptionStatus === "none"
                  }
                  currency={getPricesData.currency}
                  timeLeft={timeLeft}
                  yearlyPrice={getPricesData.yearlyPrice}
                  yearlyIntroPrice={getPricesData.yearlyIntroPrice}
                  setSelectedPlan={setSelectedPlan}
                  selectedPlan={selectedPlan}
                />

                <div className="mt-7 flex flex-col items-center justify-center gap-2">
                  <p className="text-white">
                    {t("pages.checkout.plans.cancelAnytime")}
                  </p>

                  <p className="font-bold text-xl text-white">
                    {t("pages.checkout.plans.moneyBackGuarantee")}
                  </p>

                  <SubscriptionForm
                    buttonClassName="theme-button-primary uppercase my-4"
                    buttonTitle={t("pages.checkout.plans.subscribeNow")}
                    currency={getPricesData.currency}
                    isSubmitting={isSubmitting}
                    plan={selectedPlan}
                    priceId={getPriceId(getPricesData)}
                    setIsSubmitting={setIsSubmitting}
                    price={
                      selectedPlan === "yearly"
                        ? getPricesData.yearlyPrice
                        : getPricesData.monthlyPrice
                    }
                  />

                  <p className="px-10 text-center text-[10px] text-gray-500">
                    {t("pages.checkout.plans.agreeToTerms", {
                      introPrice: getPriceInCents(true, getPricesData),
                      fullPrice: getPriceInCents(false, getPricesData),
                    })}
                  </p>
                </div>
              </section>

              <section className="mb-12">
                <Features />
              </section>

              <section className="mb-24">
                <FrequentlyAskedQuestions />
              </section>

              <section className="space-y-4 text-center">
                  <SubscriptionForm
                    buttonTitle={t("pages.checkout.plans.subscribeNow")}
                    currency={getPricesData.currency}
                    isSubmitting={isSubmitting}
                    plan={selectedPlan}
                    priceId={getPriceId(getPricesData)}
                    setIsSubmitting={setIsSubmitting}
                    price={
                      selectedPlan === "yearly"
                        ? getPricesData.yearlyPrice
                        : getPricesData.monthlyPrice
                    }
                  />

                <p className="font-bold">
                  {t("pages.checkout.plans.cancelAnytime")}
                </p>
              </section>

             
            </div>

            <LimitedTimeOfferDialog
              currency={getPricesData.currency}
              isSubmitting={isSubmitting}
              onClose={() => setIsDialogOpen(false)}
              open={isDialogOpen}
              periodAmount={12}
              periodUnit="month"
              setIsSubmitting={setIsSubmitting}
              eligibleForIntroPrice={
                getPricesData.subscriptionStatus === "none"
              }
              stripeIntroPrice={getPricesData.yearlyIntroPrice}
              stripePrice={getPricesData.yearlyPrice}
            />
          </>
        )}
      </Await>
    </Suspense>
  );
}
